import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Globe } from 'lucide-react';
import { CountryData } from '../types';

interface CountriesWidgetProps {
  timePeriod?: string;
  countries: CountryData[];
  rawData?: any; // Add raw data prop for direct processing
}

const CountriesWidget: React.FC<CountriesWidgetProps> = ({ 
  timePeriod = '7d',
  countries,
  rawData
}) => {

  // Process raw data if countries array is empty
  const processRawData = (): CountryData[] => {
    
    // Handle different data structures
    let dataToProcess = null;
    
    if (rawData && rawData.data && typeof rawData.data === 'object') {
      // Format: { success: true, data: {...} }
      dataToProcess = rawData.data;
    } else if (rawData && typeof rawData === 'object' && !rawData.success) {
      // Format: direct object with date keys
      dataToProcess = rawData;
    }
    
    if (!dataToProcess) {
      return [];
    }

    const rawDataEntries = Object.entries(dataToProcess);
    const countryTotals: { [key: string]: { visits: number; pageViews: number } } = {};

    // Process all dates
    rawDataEntries.forEach(([dateKey, dayData]: [string, any]) => {
      if (!dayData || typeof dayData !== 'object') {
        return;
      }

      // Process each country in the day's data
      Object.entries(dayData).forEach(([countryKey, countryData]: [string, any]) => {
        if (countryKey !== '(not set)' && countryData && typeof countryData === 'object') {
          const visits = countryData.count || countryData.sessions || 0;
          const pageViews = countryData.pageviews || 0;

          if (!countryTotals[countryKey]) {
            countryTotals[countryKey] = { visits: 0, pageViews: 0 };
          }

          countryTotals[countryKey].visits += visits;
          countryTotals[countryKey].pageViews += pageViews;
        }
      });
    });


    // Calculate percentages and transform to CountryData format
    const totalPageViews = Object.values(countryTotals).reduce((sum, data) => sum + data.pageViews, 0);
    
    const processedCountries: CountryData[] = Object.entries(countryTotals)
      .map(([country, data]) => ({
        country,
        visits: data.visits,
        percentage: totalPageViews > 0 ? (data.pageViews / totalPageViews) * 100 : 0
      }))
      .sort((a, b) => b.visits - a.visits)
      .slice(0, 10);

    return processedCountries;
  };
  
  // Use processed data if countries array is empty
  const displayCountries = countries && countries.length > 0 ? countries : processRawData();
  console.log('===========CountriesWidget: displayCountries:', displayCountries);
  console.log('===========CountriesWidget: countries:', countries);
  // Function to get country flag emoji
  const getCountryFlag = (countryName: string): string => {
    const countryToFlag: { [key: string]: string } = {
      'India': '🇮🇳',
      'United States': '🇺🇸',
      'United Kingdom': '🇬🇧',
      'Canada': '🇨🇦',
      'Germany': '🇩🇪',
      'France': '🇫🇷',
      'Australia': '🇦🇺',
      'Singapore': '🇸🇬',
      'United Arab Emirates': '🇦🇪',
      'Saudi Arabia': '🇸🇦',
      'Qatar': '🇶🇦',
      'Kuwait': '🇰🇼',
      'Oman': '🇴🇲',
      'Bahrain': '🇧🇭',
      'China': '🇨🇳',
      'Hong Kong': '🇭🇰',
      'Japan': '🇯🇵',
      'South Korea': '🇰🇷',
      'Indonesia': '🇮🇩',
      'Malaysia': '🇲🇾',
      'Thailand': '🇹🇭',
      'Vietnam': '🇻🇳',
      'Philippines': '🇵🇭',
      'Poland': '🇵🇱',
      'Netherlands': '🇳🇱',
      'Sweden': '🇸🇪',
      'Norway': '🇳🇴',
      'Denmark': '🇩🇰',
      'Finland': '🇫🇮',
      'Switzerland': '🇨🇭',
      'Austria': '🇦🇹',
      'Belgium': '🇧🇪',
      'Ireland': '🇮🇪',
      'Spain': '🇪🇸',
      'Italy': '🇮🇹',
      'Portugal': '🇵🇹',
      'Greece': '🇬🇷',
      'Turkey': '🇹🇷',
      'Türkiye': '🇹🇷',
      'Russia': '🇷🇺',
      'Ukraine': '🇺🇦',
      'Brazil': '🇧🇷',
      'Argentina': '🇦🇷',
      'Mexico': '🇲🇽',
      'Chile': '🇨🇱',
      'Colombia': '🇨🇴',
      'Peru': '🇵🇪',
      'South Africa': '🇿🇦',
      'Egypt': '🇪🇬',
      'Nigeria': '🇳🇬',
      'Kenya': '🇰🇪',
      'Tanzania': '🇹🇿',
      'New Zealand': '🇳🇿',
      'Israel': '🇮🇱',
      'Iran': '🇮🇷',
      'Pakistan': '🇵🇰',
      'Bangladesh': '🇧🇩',
      'Sri Lanka': '🇱🇰',
      'Nepal': '🇳🇵',
      'Myanmar': '🇲🇲',
      'Cambodia': '🇰🇭',
      'Laos': '🇱🇦',
      'Mongolia': '🇲🇳',
      'Kazakhstan': '🇰🇿',
      'Uzbekistan': '🇺🇿',
      'Azerbaijan': '🇦🇿',
      'Georgia': '🇬🇪',
      'Armenia': '🇦🇲',
      'Serbia': '🇷🇸',
      'Croatia': '🇭🇷',
      'Slovenia': '🇸🇮',
      'Slovakia': '🇸🇰',
      'Czech Republic': '🇨🇿',
      'Hungary': '🇭🇺',
      'Romania': '🇷🇴',
      'Bulgaria': '🇧🇬',
      'Lithuania': '🇱🇹',
      'Latvia': '🇱🇻',
      'Estonia': '🇪🇪',
      'Iceland': '🇮🇸',
      'Luxembourg': '🇱🇺',
      'Malta': '🇲🇹',
      'Cyprus': '🇨🇾',
      'Côte d\'Ivoire': '🇨🇮'
    };
    
    return countryToFlag[countryName] || '🌍';
  };

  return (
    <Card >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 px-0">
        <CardTitle className="text-sm font-medium">Top Countries</CardTitle>
        <Globe className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent className="px-0">
        <div className="space-y-2">
          {displayCountries && displayCountries.length > 0 ? (
            displayCountries.map((country, index) => (
              <div key={index} className="group relative">
                <div className="relative flex items-center justify-between p-2 py-1 rounded-lg overflow-hidden">
                  {/* Background Progress Bar */}
                  <div 
                    className="absolute inset-0 bg-primary/10 transition-all duration-300 ease-out"
                    style={{ width: `${country.percentage}%` }}
                  ></div>
                  
                  {/* Content on top of progress bar */}
                  <div className="relative flex items-center space-x-3 flex-1">
                    <span className="text-2xl">{getCountryFlag(country.country)}</span>
                    <span className="text-sm font-medium text-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex-1">
                      {country.country.length > 20 
                        ? `${country.country.substring(0, 10)}...${country.country.substring(country.country.length - 7)}`
                        : country.country
                      }
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {country.percentage.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground">
                {rawData ? 'Processing country data...' : 'No country data available'}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CountriesWidget;
